import { useEffect } from 'react';

// SEE-1715: the mobile shell draws the page under the device status bar, so the
// page tells it which icon colour stays legible over what's behind the bar —
// 'light' over the navy header art, 'dark' over white. Screens and open sheets
// each register a style; the most recently registered one wins, and with none
// registered (auth, onboarding — all white) the shell's 'dark' default stands.
// Old shells ignore the message; browsers have no bridge, so nothing is sent.

export type NativeStatusBarStyle = 'light' | 'dark';

const DEFAULT_STYLE: NativeStatusBarStyle = 'dark';

const registered: { style: NativeStatusBarStyle }[] = [];
let lastSent: NativeStatusBarStyle | null = null;

interface WindowWithBridge extends Window {
  ReactNativeWebView?: { postMessage: (message: string) => void };
}

function syncToShell(): void {
  const style = registered.length ? registered[registered.length - 1].style : DEFAULT_STYLE;
  if (style === lastSent) return;
  const bridge = (window as WindowWithBridge).ReactNativeWebView;
  if (!bridge) return;
  try {
    bridge.postMessage(JSON.stringify({ type: 'STATUS_BAR', style }));
    lastSent = style;
  } catch {
    // ignore — the shell keeps its current style
  }
}

export function useNativeStatusBarStyle(style: NativeStatusBarStyle, active = true): void {
  useEffect(() => {
    if (!active) return undefined;
    const entry = { style };
    registered.push(entry);
    syncToShell();
    return () => {
      registered.splice(registered.indexOf(entry), 1);
      syncToShell();
    };
  }, [style, active]);
}

/** Test-only: forget registrations and the last style sent. */
export function resetNativeStatusBarStyleForTests(): void {
  registered.length = 0;
  lastSent = null;
}
